// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StyledImgWrapper } from "@store/MobiusOverrides/Img/Img";
import validateIsDateDifferentDay from "@store/Framework/Common/Utilities/validateIsDateDifferentDay";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

interface Props {
    previewImage?: string;
    altText: string;
    aspectRatio?: string;
    mobileAspectRatio?: string;
    eventLink?: string;
    startDate?: Date;
    endDate?: Date | undefined;
    extendedStyles?: EventImageStyles;
}

export interface EventImageStyles {
    wrapper?: ClickablePresentationProps;
    dateOverImageWrapper?: InjectableCss;
    dateOverImageSeparator?: InjectableCss;
    dateOverImage?: InjectableCss;
    dateOverImageMonth?: TypographyPresentationProps;
    dateOverImageDay?: TypographyPresentationProps;
    image?: ImgProps;
}

export const eventImageStyles: EventImageStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            height: 100%;

            ${StyledImgWrapper} {
                width: 100%;
                height: 100%;
            }
        `,
    },
    dateOverImageWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: center;
            position: absolute;
            top: 0;
            left: ${pxToRem(20)};
            background-color: ${getColor("accent")};
            color: ${getColor("white")};
            font-weight: ${x => x.theme.design.typography.weight.semibold};
            text-transform: uppercase;

            ${breakpointValues([pxToRem(7, 15), pxToRem(10, 20), pxToRem(7, 15), null, pxToRem(10, 20)], "padding")}
        `,
    },
    dateOverImageSeparator: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            padding: ${pxToRem(0, 5)};
            font-weight: ${x => x.theme.design.typography.weight.base};

            ${breakpointValues(pxToRemArray([26, 36, 26, null, 36]), "line-height")}
            ${breakpointValues(pxToRemArray([20, 26, 20, null, 26]), "font-size")}
        `,
    },
    dateOverImage: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    dateOverImageMonth: {
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.small};

            ${breakpointValues(pxToRemArray([14, 20, 14, null, 20]), "font-size")}
        `,
    },
    dateOverImageDay: {
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.smaller};

            ${breakpointValues(pxToRemArray([26, 36, 26, null, 36]), "font-size")}
        `,
    },
    image: {
        css: css`
            max-width: 100%;
            width: 100%;
            height: auto;
            object-fit: cover;
        `,
    },
};

const EventImage = ({
    previewImage,
    altText,
    aspectRatio,
    mobileAspectRatio,
    eventLink,
    startDate,
    endDate,
    extendedStyles,
}: Props) => {
    const styles = useMergeProps(eventImageStyles, extendedStyles);
    const imageSrc = previewImage !== "" ? previewImage : "userfiles/DefaultImage.jpg";
    const aspectRatioValue = aspectRatio ?? "4/3";
    const mobileAspectRatioValue = mobileAspectRatio ?? aspectRatioValue;

    const imageStyle = css`
        ${styles.image?.css}
        ${breakpointValues([mobileAspectRatioValue, null, aspectRatioValue], "aspect-ratio")}
    `;

    const dateOverImage = (date: Date) => {
        const tempDate = new Date(date);
        return (
            <StyledWrapper {...styles.dateOverImage}>
                <Typography {...styles.dateOverImageMonth}>
                    {tempDate.toLocaleDateString("en-US", { month: "short", timeZone: "Etc/UTC" })}
                </Typography>
                <Typography {...styles.dateOverImageDay}>
                    {tempDate.toLocaleDateString("en-US", { day: "numeric", timeZone: "Etc/UTC" })}
                </Typography>
            </StyledWrapper>
        );
    };

    const renderImage = () => (
        <>
            {startDate && (
                <StyledWrapper {...styles.dateOverImageWrapper}>
                    {dateOverImage(startDate)}
                    {endDate && validateIsDateDifferentDay(startDate, endDate) && (
                        <>
                            <StyledWrapper {...styles.dateOverImageSeparator}>
                                <Typography>-</Typography>
                            </StyledWrapper>
                            {dateOverImage(endDate)}
                        </>
                    )}
                </StyledWrapper>
            )}
            <Img {...styles.image} css={imageStyle} src={imageSrc} altText={altText} />
        </>
    );

    return eventLink ? (
        <Clickable href={eventLink} {...styles.wrapper}>
            {renderImage()}
        </Clickable>
    ) : (
        <StyledWrapper {...styles.wrapper}>{renderImage()}</StyledWrapper>
    );
};

export default EventImage;
